import { Link } from "react-router-dom";

import simpleFirmLogo from "@/assets/simplefirm-logo.png";

import "./LandingPage.css";

// Single source of truth. Move this to your shared routes module and import it
// here + in the <Route path> declaration so the two can never drift apart.
const LOGIN_PATH = "/login";

const capabilities = [
  {
    id: "cases",
    label: "Cases",
    title: "Every matter, one record",
    copy: "Intake, treatment, litigation, and settlement live on the same case — no re-keying between systems.",
  },
  {
    id: "workflow",
    label: "Workflow",
    title: "Deadlines that chase themselves",
    copy: "Tasks, statutes, and follow-ups fire from case status, so nothing waits on someone remembering.",
  },
  {
    id: "oversight",
    label: "Oversight",
    title: "Firm-wide visibility",
    copy: "See caseload, aging, and settlement pipeline across every attorney without building a report first.",
  },
];

const LandingPage = () => (
  <main className="simple-landing">
    <a className="simple-landing__skip" href="#hero">
      Skip to content
    </a>

    <header className="simple-landing__nav">
      <Link className="simple-landing__brand" to="/" aria-label="SimpleFirm home">
        <img src={simpleFirmLogo} alt="SimpleFirm" width="150" height="44" />
      </Link>

      <nav className="simple-landing__links" aria-label="Primary">
        <a href="#capabilities">Capabilities</a>
        <a href="#platform">Why SimpleFirm</a>
        <Link className="simple-landing__sign-in" to={LOGIN_PATH}>
          Sign in
        </Link>
      </nav>
    </header>

    <section className="simple-landing__hero" id="hero">
      <div className="simple-landing__copy">
        <p className="simple-landing__kicker">
          <span aria-hidden="true" />
          Plaintiff case management
        </p>

        <h1>
          Run your firm with <em>clarity.</em>
        </h1>

        <p className="simple-landing__lede">
          One secure workspace for cases, clients, documents, deadlines, and the work that moves
          your firm forward.
        </p>

        <div className="simple-landing__actions">
          <Link className="simple-landing__primary" to={LOGIN_PATH}>
            Open your workspace
            <span aria-hidden="true">→</span>
          </Link>
          <a className="simple-landing__text-link" href="#capabilities">
            See what it does
            <span aria-hidden="true">↓</span>
          </a>
        </div>

        <ul className="simple-landing__trust">
          <li>Built for plaintiff practice</li>
          <li>Secure by design</li>
          <li>Everything in one place</li>
        </ul>
      </div>

      <figure className="simple-landing__visual">
        <div className="simple-landing__panel" aria-hidden="true">
          <div className="simple-landing__panel-top">
            <img src={simpleFirmLogo} alt="" width="48" height="24" />
            <span>Firm overview</span>
            <i />
          </div>

          <div className="simple-landing__metric">
            <p>Open cases</p>
            <strong>248</strong>
            <span>↑ 12 this month</span>
          </div>

          <div className="simple-landing__bars">
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
          </div>

          <div className="simple-landing__summary">
            <div>
              <small>Tasks today</small>
              <b>24</b>
            </div>
            <div>
              <small>Upcoming events</small>
              <b>18</b>
            </div>
          </div>

          <div className="simple-landing__activity">
            <span className="simple-landing__activity-icon">✓</span>
            <div>
              <b>Everything is on track</b>
              <small>Your team completed 38 tasks this week.</small>
            </div>
          </div>
        </div>
        <figcaption>Illustrative firm dashboard</figcaption>
      </figure>
    </section>

    <section className="simple-landing__capabilities" id="capabilities">
      <h2 className="simple-landing__section-title">
        Built around how plaintiff firms actually work
      </h2>

      <div className="simple-landing__grid">
        {capabilities.map(({ id, label, title, copy }) => (
          <article key={id} id={id === "oversight" ? "platform" : undefined}>
            <p className="simple-landing__label">{label}</p>
            <h3>{title}</h3>
            <p>{copy}</p>
          </article>
        ))}
      </div>
    </section>

    <footer className="simple-landing__footer">
      <img src={simpleFirmLogo} alt="" width="70" height="22" />
      <p>Legal work is complex. Your software shouldn’t be.</p>
      <span>© {new Date().getFullYear()} SimpleFirm</span>
    </footer>
  </main>
);

export default LandingPage;
